import * as React from 'react';
import styled from '@emotion/styled';
import { Modal, Button, TextArea, Icon, Label } from 'semantic-ui-react';
import axios from 'axios';
import Select from 'react-select';
import colors from '../../../css/colors';
import { base64ToUrl } from 'components/orders/order-information/PreviewMergedDocsStyled';
import {
  CloseButton,
  CoTypeItem,
  CoTypesList,
  DocumentSelectionArea,
  FeedbackButton,
  FeedbackButtons,
  FullPageModal,
  Header,
  HeaderTitle,
  IframeContainer,
  LeftPanel,
  LoadingOverlay,
  LoadingText,
  MainContent,
  ModalContent,
  ProcessButton,
  RightPanel,
  StickyButton,
  StyledIframe,
  StyledTextArea,
  TextAreaSection,
} from './styles';
import * as sampleData from './sample.json';
import { convertToTitleCase } from 'lib/PublicFunctions';

interface AIModalProps {
  searchId: number;
  setFormField: (value: any) => void;
  open: boolean;
  onClose: () => void;
}

interface DocumentOption {
  value: number;
  label: string;
}

interface CoTypeLine {
  id: number;
  content: string;
  pageNumber: number;
}

interface ProcessedData {
  docBase64: string;
  coTypes: CoTypeLine[];
  allLines: string;
}

const customSelectStyles = {
  container: (provided: any) => ({
    ...provided,
    width: 500,
  }),
  control: (provided: any, state: any) => ({
    ...provided,
    boxShadow: state.isFocused && '1px solid #ff5252',
    border: state.isFocused ? '1px solid #ff5252' : '1px solid #e5e5ea',
    minHeight: 48,
    '&:hover': {
      border: state.isFocused ? '1px solid #ff5252' : '1px solid #e5e5ea',
    },
  }),
};

const AIModal = (props: AIModalProps) => {
  const [documentOptions, setDocumentOptions] = React.useState<DocumentOption[]>([]);
  const [selectedDocuments, setSelectedDocuments] = React.useState<DocumentOption[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [processing, setProcessing] = React.useState(false);
  const [processedData, setProcessedData] = React.useState<ProcessedData | null>(null);
  const [documentUrl, setDocumentUrl] = React.useState<string>('');
  const [selectedCoType, setSelectedCoType] = React.useState<number | null>(null);
  const [selectedPageNumber, setSelectedPageNumber] = React.useState<number | null>(null);
  const [editedLines, setEditedLines] = React.useState<string>('');
  const [feedback, setFeedback] = React.useState<'positive' | 'negative' | null>(null);
  const [coText, setCoText] = React.useState<string>('');

  // Load documents on mount
  React.useEffect(() => {
    if (open && props.searchId) {
      loadDocuments();
    }
  }, [open, props.searchId]);

  React.useEffect(() => {
    if (processedData) {
      setEditedLines(convertToTitleCase(processedData.allLines));
    }
  }, [processedData]);

  const loadDocuments = async () => {
    setLoading(true);
    try {
      // Replace with your actual API endpoint
      const { data } = await axios.get(`/api/searches/${props.searchId}/documents`);
      const formattedDocs = data.map((doc: any) => ({
        value: doc.id,
        label: doc.customFileName,
      }));
      setDocumentOptions(formattedDocs);
    } catch (error) {
      console.error('Error loading documents:', error);
      // Handle error appropriately
    } finally {
      setLoading(false);
    }
  };

  const handleProcess = async () => {
    if (selectedDocuments.length === 0) {
      return;
    }

    setProcessing(true);
    try {
      const documentIds = selectedDocuments.map(doc => doc.value);
      const { data } = await axios.post('/api/claudeAi/processCoDocs', documentIds);
      //const data = sampleData as any;

      const url = base64ToUrl(data.mergedBase64);

      setDocumentUrl(url);
      const coText = data.coText?.replace(/<[Pp]age\s+\d+>/g, '').replace('\n\n\n', '\n\n') || '';
      setProcessedData({
        docBase64: data.mergedBase64,
        coTypes:
          data.coText
            ?.split('\n\n')
            .filter((n: string) => n)
            .map((line: string, index: number) => {
              const pageMatch = line.match(/<[Pp]age\s+(\d+)>/);
              const pageNumber = pageMatch ? parseInt(pageMatch[1]) : null;
              return {
                content: line,
                pageNumber: pageNumber,
                id: index,
              };
            }) || [],
        allLines: coText,
      });
      setCoText(coText);
    } catch (error) {
      console.error('Error processing documents:', error);
      // Handle error appropriately
    } finally {
      setProcessing(false);
    }
  };

  const handleCoTypeClick = (coType: CoTypeLine) => {
    setSelectedCoType(coType.id);
    setSelectedPageNumber(coType.pageNumber);
  };

  const handleFeedback = async (isPositive: boolean) => {
    const feedbackType = isPositive ? 'positive' : 'negative';
    setFeedback(feedbackType);

    try {
      const documentIds = selectedDocuments.map(doc => doc.value);
      await axios.post('/api/claudeAi/AddFeedbackType', {
        searchId: props.searchId,
        feedbackType,
        aiData: convertToTitleCase(processedData.allLines),
      });
    } catch (error) {
      console.error('Error submitting feedback:', error);
    }
  };

  const handleUseInfo = async () => {
    props.setFormField && props.setFormField(editedLines);
    try {
      await axios.post('/api/claudeAi/AddDataUsed', {
        searchId: props.searchId,
        dataUsed: true,
      });
    } catch (error) {
      console.error('Error submitting feedback:', error);
    }

    // Optionally submit the data
    props.onClose();
  };

  const handleClose = () => {
    // Clean up blob URL
    if (documentUrl) {
      URL.revokeObjectURL(documentUrl);
    }

    // Reset state
    setDocumentOptions([]);
    setSelectedDocuments([]);
    setProcessedData(null);
    setDocumentUrl('');
    setSelectedCoType(null);
    setEditedLines('');
    setFeedback(null);
    setCoText('');
    props.onClose();
  };
  return (
    <FullPageModal open={props.open} onClose={handleClose}>
      <ModalContent>
        <Header>
          <HeaderTitle>AI Document Processing</HeaderTitle>
          {processedData && (
            <React.Fragment>
              <FeedbackButtons>
                <FeedbackButton
                  positive
                  onClick={() => handleFeedback(true)}
                  style={{ opacity: feedback === 'positive' ? 1 : 0.6 }}
                >
                  <Icon name="thumbs up" style={{ margin: 0 }} />
                </FeedbackButton>
                <FeedbackButton
                  onClick={() => handleFeedback(false)}
                  style={{ opacity: feedback === 'negative' ? 1 : 0.6 }}
                >
                  <Icon name="thumbs down" style={{ margin: 0 }} />
                </FeedbackButton>
              </FeedbackButtons>
              <StickyButton onClick={handleUseInfo}>Use this Info</StickyButton>
            </React.Fragment>
          )}
          <CloseButton onClick={handleClose}>
            <Icon name="close" />
          </CloseButton>
        </Header>

        <MainContent>
          <LeftPanel>
            {!processedData ? (
              <DocumentSelectionArea>
                {loading ? (
                  <LoadingOverlay>
                    <Icon name="spinner" loading size="huge" />
                    <LoadingText>Loading documents...</LoadingText>
                  </LoadingOverlay>
                ) : (
                  <React.Fragment>
                    <Label>Select Documents to Process:</Label>
                    <Select
                      isMulti
                      options={documentOptions}
                      value={selectedDocuments}
                      onChange={selected => setSelectedDocuments(selected as DocumentOption[])}
                      placeholder="Select documents..."
                      styles={customSelectStyles}
                    />
                    <ProcessButton
                      onClick={handleProcess}
                      disabled={selectedDocuments.length === 0 || processing}
                      loading={processing}
                    >
                      {processing ? 'Processing...' : 'Process'}
                    </ProcessButton>
                  </React.Fragment>
                )}
              </DocumentSelectionArea>
            ) : (
              <IframeContainer>
                <StyledIframe
                  key={selectedPageNumber || 1}
                  id="document-iframe"
                  src={`${documentUrl}#page=${selectedPageNumber || 1}`}
                  title="Processed Document"
                />
              </IframeContainer>
            )}
          </LeftPanel>

          {processedData && (
            <RightPanel>
              <CoTypesList>
                <Label>CO Types:</Label>
                {processedData.coTypes.map(coType => (
                  <CoTypeItem
                    key={coType.id}
                    active={selectedCoType === coType.id}
                    onClick={() => handleCoTypeClick(coType)}
                  >
                    {coType.content}
                  </CoTypeItem>
                ))}
              </CoTypesList>

              <TextAreaSection>
                <Label>Lines (editable):</Label>
                <StyledTextArea
                  value={editedLines}
                  onChange={(e, { value }) => setEditedLines(value as string)}
                  placeholder="Edit the extracted lines here..."
                />
              </TextAreaSection>
            </RightPanel>
          )}
        </MainContent>
      </ModalContent>
    </FullPageModal>
  );
};

export default AIModal;
